//external
import React, { useContext, useEffect, useState, useRef } from "react";
import { DownloadCloud, CheckCircle } from "react-feather";
import Calendar from "react-calendar";
import { Tooltip as ReactTooltip } from "react-tooltip";
import moment from "moment";

// components
import DaySingle from "@/components/elements/planner/DaySingle";
import DayMulti from "@/components/elements/planner/DayMulti";
import Dropdown from "@/components/elements/global/Dropdown";
import ToolTipWrapper from "@/components/elements/Tooltip/ToolTipWrapper";
import ToastNotification from "@/components/elements/global/ToastNotification";
import ModalWrapper from "@/components/modals/ModalWrapper";
import AddEvent from "@/components/elements/planner/AddEvent";
import LoadingCircle from "@/components/elements/global/LoadingCircle/LoadingCircle";
import { BasicCheckboxPanel, IconLibrary, ThriveIcon } from "thrive-components";

//hooks
import useDetectClick from "@/components/hooks/useDetectClick";
import useHTTP from "@/components/hooks/useHTTP";

//context
import { PlannerContext } from "@/context/PlannerContext";

//helpers
import { BuildPath } from "@/helpers/Paths";
import { endOfDayTime, ordinal, removeTime } from "@/helpers/Dates";

//interfaces
import { plannerEvent } from "@/interfaces/planner";
import { ToastData } from "@/interfaces/LockerDetails";

//styles
import "../../css/planner.scss";
import {AnimatePresence, motion} from "framer-motion";

export interface Icalendar {
  value: number;
  label: string;
  hexCode: string;
}

const Planner = () => {
    const [toast, setToast] = useState<ToastData>({type: undefined, message: undefined, show: false});
    const [showCal, setShowCal] = useState<boolean>(false);
    const [showAddModal, setShowAddModal] = useState<boolean>(false);
    const [plusDate, setPlusDate] = useState<Date>(null);
    const [calendars, setCalendars] = useState<Icalendar[]>([]);
    const [miniCalendarEvents, setMiniCalendarEvents] = useState<plannerEvent[]>([]);
    const [showSettings, setShowSettings] = useState<boolean>(false);
    const [selectedEventsForDownload, setSelectedEventsForDownload] = useState<number[]>([]);
    const [hoverTargetId, setHoverTargetId] = useState<number>(0);
    const [downloadStatus, setDownloadStatus] = useState<"inactive" | "downloading" | "complete">("inactive");

    const {
      events,
      getEvents,
      formatEvents,
      weekBuilder,
      selectedCal,
      setSelectedCal,
      updateFilters,
      filterResults,
      calendarMessage,
    } = useContext(PlannerContext);
    const { firstDay, lastDay, days, currentWeek, back, forward, setByDay } = weekBuilder;
    const dayName = ["Sun", "Mon", "Tues", "Wed", "Thurs", "Fri", "Sat"];

    const calIdRef = useRef<number>(0);
    const { get, post, download } = useHTTP();
  
    useEffect(() => {
      if(calendarMessage?.timestamp) {
          if(calIdRef.current){
            getEvents(calIdRef.current)
          }
      }
    }, [calendarMessage?.timestamp])

    useEffect(() => {
      setSelectedEventsForDownload([]);
      calIdRef.current = selectedCal.value;
    }, [selectedCal.value]);

    useEffect(() => {
      // Get list of calendars
        get(`planner/calendar`)
        .then(({ data }) => {
            if (data.calendars) {
                const calsDropDown: Icalendar[] = data.calendars.map((cal) => {
                  return {
                    value: cal.id,
                    label: cal.name,
                    hexCode: cal.hexCode,
                  };
                });

                if (calsDropDown.length) {
                  setSelectedCal(calsDropDown[0]);
                }

                setCalendars(calsDropDown);
            }
        })
        .catch((err) => {
            console.error(err);
        });
    }, []);

    useEffect(() => {
        if (showCal) {
            getMiniCalendarEvents(new Date().toISOString());
        }
    }, [showCal]);

    useEffect(() => {
        if (selectedCal) {
            getEvents(selectedCal.value);
        }
    }, [firstDay, lastDay, selectedCal]);

    useEffect(() => {
        // Clear plus date on close
        if (!showAddModal) {
            setPlusDate(null);
        }
    }, [showAddModal]);

    useEffect(() => {
        if (downloadStatus === "complete") {
          setSelectedEventsForDownload([]);
          setTimeout(() => {setDownloadStatus("inactive")}, 3000);
        }
    }, [downloadStatus]);

    useDetectClick("nav-controls", (clickedInCal) => {if (!clickedInCal) {setShowCal(false)}});

    function miniCalChangeDate(date: Date) {
      setShowCal(false);
      setByDay(date);
    }

    function changeMonthView({ activeStartDate }) {
      // First day of month with zero time causing some timezone issues and or daylight savings,
      // since only need date in the month and not start day move forward 2 days to rule out
      const someDate = moment(activeStartDate).add(2, "days");

      getMiniCalendarEvents(someDate.toISOString());
    }

    function titleContent(e: any) {
        // Does this date in calendar have an event?
        const startOfDay = removeTime(e.date);
        const endOfDay = endOfDayTime(e.date);

        // Events on this day - single or multiday
        const dayEvents = miniCalendarEvents.filter((x) => {
            const { start, end } = x.occurrence;

            // Single Day
            if (start >= startOfDay && end <= endOfDay) {
                return true;
            }

            // Starts today - ends later
            if (
              start.getFullYear() === startOfDay.getFullYear() &&
              start.getMonth() === startOfDay.getMonth() &&
              start.getDate() === startOfDay.getDate() &&
              end > endOfDay
            ) {
                return true;
            }

            // Pass though day
            if (start < startOfDay && end > endOfDay) {
                return true;
            }

            // Ends today
            return end.getFullYear() === endOfDay.getFullYear() &&
                end.getMonth() === endOfDay.getMonth() &&
                end.getDate() === endOfDay.getDate() &&
                start < startOfDay;
        });

        if (dayEvents.length) {
            return <span></span>;
        }

        return null;
    }

    function getMiniCalendarEvents(date: string): void {
        get(`planner/calendar/${selectedCal.value}/events/month?DateWithinPeriod=${date}`)
        .then(({ data }) => {
            if (data.items) {
                const eventsWithDates: plannerEvent[] = formatEvents(data.items);
                setMiniCalendarEvents(eventsWithDates);
            }
        });
    }

    function closeRefresh() {
        if (selectedCal) {
            getEvents(selectedCal.value);
        }
        setShowAddModal(false);
    }

    function plusEvent(date: Date) {
        setPlusDate(date);
        setShowAddModal(true);
    }

    function calDefault() {
      if (!calendars.length) return "0";
      return calendars[0].label;
    }

    function handleCalChange(calId: number) {
        if (calId && calId !== selectedCal.value) {
            const getCal = calendars.find((x) => x.value === calId);

            if (getCal) {
              setSelectedCal(getCal);
            }
        }
    }

    function setEventForDownload(eventId: number) {
        if (selectedEventsForDownload.includes(eventId)) {
            setSelectedEventsForDownload(selectedEventsForDownload.filter((id) => id !== eventId));
        } else {
            setSelectedEventsForDownload([...selectedEventsForDownload, eventId]);
        }
    }

    function handleDownloadPost() {
        setDownloadStatus("downloading");
        post(`/planner/calendar/${selectedCal.value}/events/downloadReports`, {eventIds: selectedEventsForDownload,})
        .then(({data}) => {
            downloadReport(data.fileName, data.reportId)
        });
    }

    function downloadReport(fileName: string, reportId: any) {
        download(`/planner/calendar/${selectedCal.value}/events/downloadReports/${reportId}`, fileName)
        setDownloadStatus("complete");
    }

    function getDownloadButtonText() {
      switch (downloadStatus) {
        case "inactive":
          return (
            <>
              <DownloadCloud size={12} color="white" />{" "}
              <span style={{ marginLeft: "5px", paddingTop: "1px" }}>
                Download Report(s)
              </span>
            </>
          );
        case "downloading":
          return (
            <>
              <LoadingCircle />{" "}
              <span style={{ marginLeft: "5px", paddingTop: "1px" }}>
                Generating Report
              </span>
            </>
          );
        case "complete":
          return (
            <>
              <CheckCircle size={12} color="#10b981" />
              <span style={{ marginLeft: "5px", paddingTop: "1px" }}>Done</span>
            </>
          );
      }
    }

    if (!firstDay || !lastDay) {
      return null;
    }

    return (
        <>
          <div className="planner-container">
              <div className="title-container">
                <h2 className="title">Events</h2>

                <ToolTipWrapper
                  id="planner-top-tooltip"
                  className="insights-top-tooltip"
                >
                  <ReactTooltip
                    place="top"
                    className="thrive-tooltip"
                    delayHide={1000}
                    id="planner-top-tooltip"
                  >
                    <span>View, create and manage your Thrive Planner events</span>
                  </ReactTooltip>
                </ToolTipWrapper>
              </div>

              {!!selectedCal.value 
                  ? 
                      (<>
                          <button className="btn add-event" onClick={() => setShowAddModal(true)}>
                              Add New Event
                          </button>

                          <div className="calendar-container">
                              <div className="controls">
                                  <div className="calendar-select">
                                      {calendars.length > 0 && (
                                          <Dropdown
                                              className={"calendar-select-dropdown"}
                                              options={calendars}
                                              defaultValue={calDefault()}
                                              setValue={(opt) => handleCalChange(opt as number)}
                                              placeholder={selectedCal?.label ? selectedCal.label : ""}
                                          />
                                      )}
                                  </div>

                                  <nav className="nav-controls">
                                      <button aria-label="back" className="arrow" onClick={() => {back(); setShowCal(false)}}>
                                          <img className="spinner" src={`${BuildPath()}/images/planner/left.svg`} alt="Back" />
                                      </button>

                                      <button className="date-range" onClick={() => setShowCal(true)} >
                                          <h2>
                                              {firstDay.toLocaleDateString("en-gb", {month: "long", day: "numeric"})}
                                              {" "}-{" "}
                                              {lastDay.toLocaleDateString("en-gb", {month: "long", day: "numeric"})}
                                          </h2>
                                        
                                          <span>Week {currentWeek}</span>
                                      </button>

                                      <button aria-label="forward" className="arrow" onClick={() => {forward(); setShowCal(false)}}>
                                          <img className="spinner" src={`${BuildPath()}/images/planner/right.svg`} alt="Forward"/>
                                      </button>

                                      {showCal && (
                                          <div className="plan-mini-calendar">
                                              <Calendar
                                                  onClickDay={miniCalChangeDate}
                                                  onClickWeekNumber={(weeknumber, date) => miniCalChangeDate(date)}
                                                  tileContent={titleContent}
                                                  showWeekNumbers={true}
                                                  showNeighboringMonth={false}
                                                  maxDetail="month"
                                                  minDetail="month"
                                                  prev2Label=""
                                                  next2Label=""
                                                  onActiveStartDateChange={changeMonthView}
                                                  locale={days[0].getDay() === 0 ? 'en-US' : 'en-GB'}
                                              />
                                          </div>
                                      )}
                                  </nav>

                                  <div className="btn-container">
                                      {(selectedEventsForDownload.length > 0 || downloadStatus != "inactive") && (
                                          <button className={`btn download-reports ${downloadStatus}`} onClick={handleDownloadPost} >
                                              {getDownloadButtonText()}
                                          </button>
                                      )}

                                      <button className="btn settings" onClick={() => setShowSettings(!showSettings)} aria-label="View filter options">
                                          <ThriveIcon iconName={IconLibrary.FILTERS} color="var(--thriveWhite)" sizeInPx="24"/>
                                      </button>
                                  </div>
                              </div>
                              <AnimatePresence>
                                  {showSettings &&
                                      <motion.div
                                          initial={{ height: 0, opacity: 0 }}
                                          animate={{ height: 'auto', opacity: 1 }}
                                          exit={{ height: 0, opacity: 0 }}
                                          transition={{ duration: 0.3 }}
                                          style={{ overflow: 'hidden' }}
                                      >
                                          <BasicCheckboxPanel
                                              panelTitle={"Show:"}
                                              items={[
                                                  {
                                                      id:"ActiveEvents",
                                                      isChecked: filterResults.showActive,
                                                      onChange: () => updateFilters({...filterResults, showActive: !filterResults.showActive}),
                                                      label:"Active Events",
                                                      ariaLabel:"Active Events",
                                                      isDisabled:!showSettings,
                                                  },
                                                  {
                                                      id:"CancelledEvents",
                                                      isChecked:filterResults.showCancelled,
                                                      onChange:() => updateFilters({...filterResults, showCancelled: !filterResults.showCancelled}),
                                                      label:"Cancelled Events",
                                                      ariaLabel:"Cancelled Events",
                                                      isDisabled:!showSettings
                                                  }
                                              ]}
                                          />
                                      </motion.div>

                                  }
                              </AnimatePresence>


                              <div className={`calendar ${[downloadStatus]}`}>
                                  <header>
                                      <ul>
                                          {days.map((day:any, i:number) => (
                                              <li key={i}>
                                                  {dayName[day.getDay()]} {ordinal(day.getDate())}
                                              </li>
                                          ))}
                                      </ul>
                                  </header>

                                  <div className="days-single days-multi">
                                      <ul>
                                          {days.map((date: Date, i: number) => (
                                              <DayMulti
                                                  date={date}
                                                  events={events}
                                                  key={`days-multi-${i}`}
                                                  index={i}
                                                  setToast={setToast}
                                                  selectedEvents={selectedEventsForDownload}
                                                  setSelectedEvents={setEventForDownload}
                                                  hoverTargetId={hoverTargetId}
                                                  setHoverTargetId={setHoverTargetId}
                                              />
                                          ))}
                                      </ul>
                                  </div>

                                  <div className="days-single">
                                      <ul>
                                          {days.map((date: Date, i: number) => (
                                              <DaySingle
                                                date={date}
                                                events={events}
                                                key={`days-single-${i}`}
                                                plusEvent={plusEvent}
                                                setToast={setToast}
                                                selectedEvents={selectedEventsForDownload}
                                                setSelectedEvents={setEventForDownload}
                                                hoverTargetId={hoverTargetId}
                                                setHoverTargetId={setHoverTargetId}
                                              />
                                          ))}
                                      </ul>
                                  </div>
                              </div>
                          </div>
                    </>) 
                :   (<div className="no-cals">There are no calendars currently set up</div>)}
            </div>

            <ModalWrapper
                className="add-event-modal"
                isOpen={showAddModal}
                onRequestClose={() => setShowAddModal(false)}
                escClose={showAddModal}
                hideCloseButton={true}
            >
                <AddEvent
                    plusDate={plusDate}
                    closeModal={closeRefresh}
                    setToast={setToast}
                />
            </ModalWrapper>

            <ToastNotification show={toast?.show} closeToast={() => setToast({ ...toast, show: false })} messageType={toast?.type}>
                {toast?.message}
            </ToastNotification>
        </>
    );
};

export default Planner;
